import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import * as v from 'valibot';
import type RouterService from '@ember/routing/router-service';
import type { Store as StoreService } from '@warp-drive/core';

import resetPasswordFormSchema from 'tio-common/validation-schema/forms/reset-password-form';
import { resetPasswordRequest } from 'tio-data/requests/login';
import Button from 'tio-ui/components/button';
import Form from 'tio-ui/components/form';
import Input from 'tio-ui/components/input';
import type { FormResult } from 'tio-ui/components/form';
import type { NotificationsService } from 'tio-ui/components/notifications';
import EmailLookupWrapper from 'tio-employee/components/auth/email-lookup-wrapper';
import ErrorModal from 'tio-employee/components/auth/reset-password/error-modal';
import type ResetPasswordController from 'tio-employee/controllers/reset-password';
import type PreRegisterService from '../../services/pre-register.ts';

interface ResetPasswordRouteComponentSignature {
  Args: {
    controller: ResetPasswordController;
    model?: { error: string };
  };
}

interface PasswordFormData {
  password: string;
  passwordConfirmation: string;
}

export default class ResetPasswordRoute extends Component<ResetPasswordRouteComponentSignature> {
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare preRegister: PreRegisterService;
  @service declare notifications: NotificationsService;

  @tracked formData: PasswordFormData = {
    password: '',
    passwordConfirmation: '',
  };

  get isTokenInvalid() {
    return !!this.args.model?.error;
  }

  get passwordMinLength() {
    return this.preRegister.settings?.security?.passwordMinLength || 8;
  }

  get schema() {
    return resetPasswordFormSchema(this.passwordMinLength);
  }

  validate = (data: PasswordFormData) => {
    const r = v.safeParse(this.schema, data);
    return r.success ? {} : (v.flatten(r.issues).nested ?? {});
  };

  handleFormChange = (result: FormResult<PasswordFormData>) => {
    this.formData = result.data;
  };

  resetPassword = dropTask(async () => {
    try {
      await this.store.request(
        resetPasswordRequest({
          password: this.formData.password,
          passwordConfirmation: this.formData.passwordConfirmation,
          token: this.args.controller.token,
        })
      );
      this.router.transitionTo('reset-password.confirmation');
    } catch (e) {
      const errorMessage = e?.errors?.length ? e.errors[0] : 'An Error Occurred';
      this.notifications.add(errorMessage, {
        appearance: 'error',
      });
    }
  });

  handleSubmit = () => {
    this.resetPassword.perform();
  };

  onSubmitModal = () => {
    this.router.transitionTo('login');
  };

  <template>
    <div class="tio-col-flush h-screen container mx-auto w-full overflow-auto">
      <EmailLookupWrapper @route="reset-password">
        <h1 class="text-xl my-4 text-center">{{t "login.reset_password_title"}}</h1>
        <p class="mb-4 text-center">{{t "login.reset_password_body"}}</p>
        <Form
          @data={{this.formData}}
          @validate={{this.validate}}
          @onChange={{this.handleFormChange}}
          @onSubmit={{this.handleSubmit}}
          data-test-reset-password-form
          as |form|
        >
          <div class="mb-10">
            {{#let (form.field "password") as |f|}}
              <Input
                @label={{t "login.new_password"}}
                @description={{t "login.minimum_characters" min=this.passwordMinLength}}
                @type="password"
                @value={{f.value}}
                @onChange={{f.onChange}}
                @onBlur={{f.onBlur}}
                @errors={{f.errors}}
                data-test-password-field
              />
            {{/let}}
          </div>
          <div class="mb-10">
            {{#let (form.field "passwordConfirmation") as |f|}}
              <Input
                @label={{t "login.confirm_new_password"}}
                @description={{t "login.minimum_characters" min=this.passwordMinLength}}
                @type="password"
                @value={{f.value}}
                @onChange={{f.onChange}}
                @onBlur={{f.onBlur}}
                @errors={{f.errors}}
                data-test-password-confirmation-field
              />
            {{/let}}
          </div>
          <div class="mb-6 text-center">
            <Button
              @isRunning={{this.resetPassword.isRunning}}
              @intent="primary"
              type="submit"
              class="w-36"
              data-test-submit-button
            >
              {{t "common.submit"}}
            </Button>
          </div>
          <div data-test-login-here class="mb-6 text-center">
            {{t "login.need_to_register"}}
            <a class="underline tio-fg-info" href="/register">
              {{t "login.register_new_account"}}
            </a>
          </div>
        </Form>
        <ErrorModal @isOpen={{this.isTokenInvalid}} @onSubmit={{this.onSubmitModal}} />
      </EmailLookupWrapper>
    </div>
  </template>
}
